import { Brand, Frame, Img } from "../headliner/DeckShell";
import { CinematicCover } from "../headliner/CinematicCover";
import { LogoStamp } from "../headliner/brands";
import { brandOf, Wordmark } from "./brands";

/* Quantum Partnership — slides 01–06. Papir-tema, én mørk sceneslide. Ingen tall vi ikke kan stå for; kvante «vist, ikke lovet». */

/* 01 Cover */
export const Cover = ({ mottaker }) => {
  const b = brandOf(mottaker);
  const name = mottaker || "the Quantum Partner";
  return (
    <CinematicCover
      lockup={<p className="bd-lockup" data-testid="qd-lockup"><Brand size={19} /><i>×</i><Wordmark name={mottaker || "Quantum Partner"} h={24} /></p>}
      label="Quantum Partnership"
      title={["One day. One room.", <span className="acc">Quantum at work.</span>]}
      sub={<>700 of Norway's decision-makers in one room. One real optimisation problem from Norwegian industry — worked through with <b>{name}</b>, and shown on the main stage.</>}
      index={[
        ["Date", "13 April 2027"],
        ["Venue", "Grieghallen, Bergen"],
        [mottaker ? "Prepared for" : "Audience", mottaker || "700 seats · invitation-only", mottaker ? "prep" : "", "hd-prepared"],
      ]}
      className={b.color ? "bd-branded" : ""}
    />
  );
};

/* 04 Who is already at the table — sosialt bevis for en kald mottaker: Kevin (foreslått, tilgjengelighet bekreftet) og Replit (Build Partner, ja i prinsippet).
   Statusene står på sliden, ikke bare i fotnoten. Samme visuelle blokk (300×340) for begge: portrett vs. ordmerke på tonet plate. */
const TABLE = [
  {
    id: "kevin", k: "The headliner", n: "Kevin O'Leary", r: "Investor · Business · proposed opening keynote",
    st: "Availability confirmed for 13 April 2027 · not yet contracted",
    d: "Opens the day on the main stage. Direct, commercial, unsentimental — the register the whole day is built in.",
    visual: <Img src="/images/headliner/kevin-src-hi.webp" alt="Kevin O'Leary" draggable={false} />,
  },
  {
    id: "replit", k: "The Build Partner", n: "Replit", r: "Official Build Partner · the Build Lab and the live build on the main stage",
    st: "Agreed in principle · agreement being formalised October 2026",
    d: "One real business problem turned into working software, live, in front of the room. USD 9bn valuation (Series D, March 2026) · 50m+ users.",
    visual: <i className="mask replit" style={{ "--m": "url(/logos/partners/replit.svg)" }} aria-label="Replit" />,
  },
];

export const AtTheTable = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Already at the table" mottaker={mottaker} className="qd-table hd-light">
    <h2 className="hd-h2" data-testid="qd-table-h">Who is already<br />at the table.</h2>
    <div className="qd-table-cols">
      {TABLE.map((t) => (
        <section className={`qd-table-col qd-table-${t.id}`} key={t.id} data-testid={`qd-table-${t.id}`}>
          <span className="k">{t.k}</span>
          <div className="row">
            <div className="ph">{t.visual}</div>
            <div className="txt">
              <span className="n">{t.n}</span>
              <span className="r">{t.r}</span>
              <span className="st on">{t.st}</span>
              <p className="d">{t.d}</p>
            </div>
          </div>
        </section>
      ))}
    </div>
    <p className="bd-foot" data-testid="qd-table-foot"><b>One company per category.</b> The Quantum position sits next to these — on the same stage, in the same room.</p>
  </Frame>
);

/* 05 The stage — salen med mottakerens merke på LED-skjermen (generert konseptvisualisering) */
export const StageScene = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The stage" mottaker={mottaker} className="bd-scene hd-dark">
    <Img className="bd-scene-img" src={brandOf(mottaker).plate} alt="" draggable={false} fetchPriority="high" data-testid="qd-scene-img" />
    <div className="bd-scene-shade" aria-hidden />
    <div className="bd-scene-txt">
      <h2 data-testid="qd-scene-h">Inside this room, {mottaker} owns the moment<br />quantum becomes a business decision.</h2>
      <p data-testid="qd-scene-p">700 of Norway's decision-makers. One main stage. A real case from Norwegian industry, worked through with {mottaker} — and shown to everyone: the problem, the approach, the result.</p>
    </div>
    <p className="bd-scene-cap" data-testid="qd-scene-cap">Concept visualisation · Grieghallen, Bergen · 13 April 2027</p>
  </Frame>
);

/* 04 The room — publikumsbevis */
const ROOM = [
  ["C-suite & senior leaders", "The people who approve what gets optimised — and what gets bought."],
  ["Operations & technology", "COOs, CIOs, CTOs, heads of logistics, planning and data — the ones who deploy."],
  ["Investors & builders", "Founders, scale-ups and the capital behind them."],
  ["Public sector & policy", "The institutions that set the pace for advanced computing in Norway."],
];

export const Room = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The room" mottaker={mottaker} className="bd-room">
    <div className="bd-big bd-room-big" data-testid="qd-room-700">700</div>
    <p className="bd-biglab bd-room-lab">seats · invitation-led · Grieghallen, Bergen</p>
    <p className="bd-room-cur" data-testid="qd-room-cur">Curated for people with the authority to buy, build and deploy — and operations worth optimising.</p>
    <ul className="hd-ledger bd-ledger bd-room-ledger" data-testid="qd-room-segments">
      {ROOM.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
    <p className="bd-foot" data-testid="qd-room-foot"><b>700 people who today read about quantum could leave knowing which of their own problems it can solve.</b> For {mottaker}: the operators, the buyers and the boards that approve both — in one room, for one day.</p>
  </Frame>
);

/* 07 Quantum, plainly — summitens redaksjonelle holdning: vist, ikke lovet. Tre ting vi sier rett ut — også til partneren. */
const PLAIN = [
  ["In production today", "Hybrid quantum-classical optimisation — scheduling, routing, network and resource allocation — runs in live operations at large companies today.", "This is the part of quantum a board can act on now. It is also the part the main stage will show."],
  ["Not yet", "Fault-tolerant, general-purpose quantum computing at scale. It is coming; it is not here.", "We will say so from the stage. The room trusts the rest of the day because of it."],
  ["The question for a Norwegian board in 2027", "Not «should we do quantum?» — but: which of our hardest problems are optimisation problems, and what is it worth to solve them better?", "That question is what the case, the briefing and the Lab are built to answer."],
];

export const Plainly = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Quantum, plainly" mottaker={mottaker} className="qd-plain">
    <h2 className="hd-h2" data-testid="qd-plain-h">Quantum, plainly.</h2>
    <p className="qd-plain-lede" data-testid="qd-plain-lede">Most rooms hear quantum either as science fiction or as a sales pitch. This one will hear neither. Three things we say plainly — from the stage, and to {mottaker} before we start.</p>
    <div className="qd-plain-cols" data-testid="qd-plain-points">
      {PLAIN.map(([k, v, n], i) => (
        <section className="qd-plain-col" key={k}>
          <span className="k"><i>0{i + 1}</i>{k}</span>
          <p className="v">{v}</p>
          <p className="n">{n}</p>
        </section>
      ))}
    </div>
    <p className="bd-foot" data-testid="qd-plain-foot">The summit's theme is <b>The Next Shift</b>. Quantum belongs in it — on one condition: it is shown, not promised.</p>
  </Frame>
);

/* 06 Why <mottaker> — fit, ikke tall */
export const WhyYou = ({ nr, mottaker }) => (
  <Frame nr={nr} label={`Why ${mottaker}`} mottaker={mottaker} className="bd-whyyou qd-whyyou">
    <h2 className="bd-h96" data-testid="qd-whyyou-h">Why {mottaker}.</h2>
    <ul className="hd-ledger bd-ledger" data-testid="qd-whyyou-points">
      {brandOf(mottaker).why(mottaker).map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
    <LogoStamp name={mottaker} sub="Quantum Partner · The quantum position · Norwegian AI Summit 2027" />
    <p className="bd-foot" data-testid="qd-whyyou-foot">Not «here is what {mottaker} does» — here is why this room is strategically interesting for {mottaker}.</p>
  </Frame>
);
